import type { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';

import { z } from 'zod';

import { useLocalState } from '#hooks/local-state.ts';

const columnVisibilitySchema = z.record(z.string(), z.boolean());

interface UseColumnVisibilityOptions {
  /**
   * Unique key for localStorage to prevent conflicts between different grids
   */
  storageKey: string;
  /**
   * Default column visibility model. If not provided, all columns are visible by default.
   * This allows you to set specific columns as hidden by default.
   */
  defaultVisibility?: GridColumnVisibilityModel;
}

/**
 * Custom hook to manage DataGridPro column visibility with localStorage persistence.
 *
 * Loads initial column visibility from localStorage on mount and automatically saves
 * changes when users toggle column visibility. No external synchronization needed.
 */
export function useColumnVisibility({
  storageKey,
  defaultVisibility = {},
}: UseColumnVisibilityOptions) {
  const [model, setModel] = useLocalState({
    key: storageKey,
    defaultValue: defaultVisibility,
    schema: columnVisibilitySchema,
  });

  return {
    model,
    onChange: setModel,
  };
}
